import { useCallback, useMemo, useState } from 'react';

export type MenuKey = string | number;

export type MenuNodeType = 'Link' | 'Shortcut' | 'Container';

export type MenuRenderer = (node: MenuNode, isExpanded: boolean) => React.ReactNode;

export interface MenuNode {
    id: MenuKey;
    parentId?: MenuKey;
    name: string;
    url: string | undefined;
    segmentUrl: string | undefined;
    ancestors: MenuNode[];
    nodes: MenuNode[];
    type: MenuNodeType;
    renderBody?: MenuRenderer;
    displayName?: string;
}

const toggle = (expanded: MenuKey[], toExpandId: MenuKey, parentId: MenuKey | undefined) => {
    let removalCount = expanded.length;
    for (let i = expanded.length - 1; i >= 0; i -= 1) {
        // if previous is a parent for current expand, just expand yourself
        if (expanded[i] === parentId) {
            const prevExpands = expanded.slice(0, removalCount);
            return [...prevExpands, toExpandId];
        }

        // if previous is the same for current expand, remove expand
        if (expanded[i] === toExpandId) {
            return expanded.slice(0, i);
        }

        // if previous is not a parent for current expand, that means we have expanded something from above, set index and check another previous
        removalCount = i;
    }

    return [...expanded.slice(0, removalCount), toExpandId];
};

const getNodesTrailById = (
    rootNode: MenuNode | null,
    toExpandId: MenuKey,
    trail: MenuKey[] = [],
): MenuKey[] => {
    if (!rootNode) {
        return [];
    }

    const nodes = rootNode?.nodes || [];

    if (rootNode.id === toExpandId) {
        return [...trail, toExpandId];
    }

    let expanded: MenuKey[] = [];
    for (let i = 0; expanded.length === 0 && i < nodes.length; i += 1) {
        expanded = getNodesTrailById(nodes[i], toExpandId, [...trail, rootNode.id]);
    }
    return expanded;
};

const resolveNodeTrailByUrlSegments = (
    node: MenuNode,
    urlSegments: string[],
    idTrail: MenuKey[],
    // strict matching type ensures that the match of segments is exact from top to bottom, used for fully loaded menus that may include the same segments several times inside different nodes.
    // lenient just traverses down the line while not ensuring the parents paths are correct, needed for partial menus like global menu
    matchingType: 'strict' | 'lenient',
) => {
    if (!node || !urlSegments?.length) {
        return;
    }

    const segments = [...urlSegments];

    if (
        matchingType === 'strict' &&
        node.segmentUrl?.toLocaleLowerCase() !== segments[0].toLocaleLowerCase()
    ) {
        return;
    }

    for (let i = 0; i < segments.length; i += 1) {
        if (node.segmentUrl?.toLocaleLowerCase() === segments[i]?.toLocaleLowerCase()) {
            idTrail.push(node.id);
            segments.splice(i, 1);
        }
    }

    if (segments.length) {
        node.nodes.forEach((x) =>
            resolveNodeTrailByUrlSegments(x, segments, idTrail, matchingType),
        );
    }
};

const traverseAndMap = (node: MenuNode, map: Map<MenuKey, MenuNode>) => {
    // Add the current node to the map
    map.set(node.id, node);

    // Recursively call this function for each child node
    node.nodes.forEach((_node) => traverseAndMap(_node, map));
};

export const createNodeMap = (root: MenuNode) => {
    const map = new Map<MenuKey, MenuNode>();
    traverseAndMap(root, map);
    return map;
};

const getNodesTrailByUrlSegments = (
    rootNode: MenuNode | null,
    urlSegments: string[],
    matchingType: 'strict' | 'lenient' = 'lenient',
) => {
    if (!rootNode) return [];

    const idTrail: number[] = [];
    resolveNodeTrailByUrlSegments(rootNode, urlSegments, idTrail, matchingType);
    return idTrail;
};

const useMenuState = (rootMenuNode: MenuNode | undefined) => {
    const [expandedNodesTrail, setExpandedNodesTrail] = useState<MenuKey[]>([]);
    const [activeNodesTrail, setActiveNodesTrail] = useState<MenuKey[]>([]);

    const setExpandedNodesTrailById = useCallback(
        (activeId: MenuKey) => {
            if (rootMenuNode) {
                setExpandedNodesTrail(getNodesTrailById(rootMenuNode, activeId));
            }
        },
        [rootMenuNode],
    );

    const setActiveNodesTrailById = useCallback(
        (activeId: MenuKey) => {
            if (rootMenuNode) {
                setActiveNodesTrail(getNodesTrailById(rootMenuNode, activeId));
            }
        },
        [rootMenuNode],
    );

    const setExpandedNodesTrailByUrlSegments = useCallback(
        (_urlSegments: string[], matchingType: 'strict' | 'lenient' = 'lenient') => {
            if (rootMenuNode) {
                setExpandedNodesTrail(
                    getNodesTrailByUrlSegments(rootMenuNode, _urlSegments, matchingType),
                );
            }
        },
        [rootMenuNode],
    );

    const setActiveNodesTrailByUrlSegments = useCallback(
        (_urlSegments: string[], matchingType: 'strict' | 'lenient' = 'lenient') => {
            if (rootMenuNode) {
                setActiveNodesTrail(
                    getNodesTrailByUrlSegments(rootMenuNode, _urlSegments, matchingType),
                );
            }
        },
        [rootMenuNode],
    );

    const toggleExpandedNode = useCallback((id: MenuKey, parentId: MenuKey | undefined) => {
        setExpandedNodesTrail((activeNodeId) => toggle(activeNodeId, id, parentId));
    }, []);

    const nodeMap = useMemo(() => {
        if (rootMenuNode) {
            return createNodeMap(rootMenuNode);
        }

        return new Map<MenuKey, MenuNode>();
    }, [rootMenuNode]);

    return {
        nodeMap,
        activeNodesTrail,
        expandedNodesTrail,
        toggleExpandedNode,
        setExpandedNodesTrailById,
        setActiveNodesTrailById,
        setExpandedNodesTrailByUrlSegments,
        setActiveNodesTrailByUrlSegments,
    };
};

export default useMenuState;
